import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  GripVertical,
  Plus,
  Save,
  Trash2,
  Undo2,
} from 'lucide-react'
import {
  apiErrorMessage,
  fetchComponentMaster,
  updateComponentMaster,
} from '@/services/api'
import {
  COMPONENT_CATEGORIES,
  type ComponentCategory,
  type ComponentDef,
} from '@/types/machine'
import ConfirmDialog from '@/components/ConfirmDialog'

const inputClass =
  'w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent'

// 編集中の1行。originalName はサーバ上の現在名（改名検出に使う。新規追加は undefined）
interface EditableDef extends ComponentDef {
  id: string
  originalName?: string
}

// 部品マスタの編集画面。追加・削除・改名は保存時に全機材の構成部品へ一括反映される
export default function ComponentsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const masterQuery = useQuery({
    queryKey: ['componentMaster'],
    queryFn: fetchComponentMaster,
  })

  // 編集中のマスタ（保存するまでサーバへは反映しない）
  const [defs, setDefs] = useState<EditableDef[] | null>(null)
  const [newNames, setNewNames] = useState<Record<ComponentCategory, string>>({
    controlBox: '',
    other: '',
  })
  const [error, setError] = useState<string | null>(null)
  const [savedMessage, setSavedMessage] = useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)

  // 行の React key 用のローカルID（名前は編集可能なので key に使えない）
  const idRef = useRef(0)
  const toEditable = (list: ComponentDef[]): EditableDef[] =>
    list.map((c) => ({ ...c, id: `c${idRef.current++}`, originalName: c.name }))

  useEffect(() => {
    if (masterQuery.data && defs === null) {
      setDefs(toEditable(masterQuery.data))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterQuery.data, defs])

  const saved = useMemo(() => masterQuery.data ?? [], [masterQuery.data])
  const dirty = useMemo(() => {
    if (!defs) return false
    const project = (list: { name: string; category: string }[]) =>
      JSON.stringify(list.map((c) => [c.name, c.category]))
    return (
      project(defs) !== project(saved) ||
      defs.some((d) => d.originalName && d.originalName !== d.name)
    )
  }, [defs, saved])

  // 改名（削除+追加ではなく、全機材の部品名を付け替える）
  const renames = useMemo(
    () =>
      (defs ?? [])
        .filter((d) => d.originalName && d.originalName !== d.name.trim())
        .map((d) => ({ from: d.originalName!, to: d.name.trim() })),
    [defs]
  )
  // 保存時に全機材から削除される部品（確認ダイアログで警告する）
  const removedNames = useMemo(() => {
    if (!defs) return []
    const keptOriginals = new Set(
      defs.map((d) => d.originalName).filter(Boolean)
    )
    return saved.filter((c) => !keptOriginals.has(c.name)).map((c) => c.name)
  }, [defs, saved])

  const saveMutation = useMutation({
    mutationFn: () =>
      updateComponentMaster(
        defs!.map((d) => ({ name: d.name.trim(), category: d.category })),
        renames
      ),
    onSuccess: (result) => {
      setError(null)
      setSavedMessage(
        t('componentsPage.saved', { count: result.machinesUpdated })
      )
      setDefs(toEditable(result.components))
      queryClient.setQueryData(['componentMaster'], result.components)
      // 全機材の構成部品が変わるため一覧・詳細のキャッシュも更新する
      queryClient.invalidateQueries({ queryKey: ['machines'] })
      queryClient.invalidateQueries({ queryKey: ['machine'] })
    },
    onError: (err) =>
      setError(apiErrorMessage(err) ?? t('componentsPage.saveError')),
    onSettled: () => setConfirmOpen(false),
  })

  const handleAdd = (category: ComponentCategory) => {
    const name = newNames[category].trim()
    if (!name || !defs) return
    if (defs.some((c) => c.name.trim() === name)) {
      setError(t('componentsPage.duplicate', { name }))
      return
    }
    setError(null)
    setSavedMessage(null)
    setDefs([...defs, { id: `c${idRef.current++}`, name, category }])
    setNewNames((prev) => ({ ...prev, [category]: '' }))
  }

  const handleRename = (id: string, name: string) => {
    if (!defs) return
    setSavedMessage(null)
    setDefs(defs.map((d) => (d.id === id ? { ...d, name } : d)))
  }

  const handleRemove = (id: string) => {
    if (!defs) return
    setError(null)
    setSavedMessage(null)
    setDefs(defs.filter((c) => c.id !== id))
  }

  // 同じ区分内で1つ上/下の部品と位置を入れ替える。マスタの並び順が
  // そのまま一覧の列順・詳細画面の表示順になる（保存で全ユーザーに反映）
  const handleMove = (id: string, delta: -1 | 1) => {
    if (!defs) return
    const target = defs.find((d) => d.id === id)
    if (!target) return
    const categoryIds = defs
      .filter((d) => d.category === target.category)
      .map((d) => d.id)
    const neighborId = categoryIds[categoryIds.indexOf(id) + delta]
    if (!neighborId) return
    const i = defs.findIndex((d) => d.id === id)
    const j = defs.findIndex((d) => d.id === neighborId)
    const next = [...defs]
    ;[next[i], next[j]] = [next[j], next[i]]
    setSavedMessage(null)
    setDefs(next)
  }

  // ドラッグ＆ドロップの並び替え。ドラッグ中の行が別の行に重なったら
  // その位置へ移動する（リアルタイムに並び替え、同じ区分内のみ）
  const [dragId, setDragId] = useState<string | null>(null)
  const handleDragOverRow = (overId: string) => {
    if (!defs || !dragId || dragId === overId) return
    const from = defs.findIndex((d) => d.id === dragId)
    const to = defs.findIndex((d) => d.id === overId)
    if (from < 0 || to < 0 || defs[from].category !== defs[to].category) return
    const next = [...defs]
    const [moved] = next.splice(from, 1)
    next.splice(to, 0, moved)
    setSavedMessage(null)
    setDefs(next)
  }

  const handleSave = () => {
    if (!defs) return
    // 空名・重複名は保存前に弾く
    const names = defs.map((d) => d.name.trim())
    if (names.some((n) => !n) || new Set(names).size !== names.length) {
      setError(t('componentsPage.invalidNames'))
      return
    }
    setError(null)
    if (removedNames.length > 0) {
      setConfirmOpen(true)
    } else {
      saveMutation.mutate()
    }
  }

  const handleBack = () => {
    if (dirty && !window.confirm(t('detail.confirmLeave'))) return
    navigate('/')
  }

  if (masterQuery.isError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <p className="text-sm text-red-600">{t('componentsPage.fetchError')}</p>
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-1.5 px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common.back')}
        </button>
      </div>
    )
  }

  if (masterQuery.isLoading || !defs) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        <span className="ml-3 text-gray-500">{t('common.loading')}</span>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={handleBack}
          className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap shrink-0"
        >
          <ArrowLeft className="h-4 w-4" />
          {t('common.back')}
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              if (!window.confirm(t('detail.undoConfirm'))) return
              setDefs(toEditable(saved))
              setError(null)
            }}
            disabled={!dirty}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap shrink-0"
          >
            <Undo2 className="h-4 w-4" />
            {t('detail.undo')}
          </button>
          <button
            onClick={handleSave}
            disabled={!dirty || saveMutation.isPending}
            className="flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap shrink-0"
          >
            <Save className="h-4 w-4" />
            {saveMutation.isPending ? t('detail.saving') : t('detail.save')}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
          {error}
        </div>
      )}
      {savedMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
          {savedMessage}
        </div>
      )}
      {dirty && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md text-sm text-yellow-700">
          {t('componentsPage.unsavedHint')}
        </div>
      )}

      <p className="mb-4 text-xs text-gray-500">
        {t('componentsPage.orderHint')}
      </p>

      <div className="space-y-6">
        {COMPONENT_CATEGORIES.map((category) => (
          <section
            key={category}
            className="bg-white rounded-lg border border-gray-200 shadow-sm p-6"
          >
            <h3 className="text-sm font-semibold text-gray-900 mb-4">
              {t(`componentCategory.${category}`)}
            </h3>
            <ul className="divide-y divide-gray-100 border border-gray-200 rounded-md mb-3">
              {defs.filter((c) => c.category === category).length === 0 && (
                <li className="px-4 py-3 text-sm text-gray-400">
                  {t('componentsPage.empty')}
                </li>
              )}
              {defs
                .filter((c) => c.category === category)
                .map((component, pos, list) => {
                  const renamed =
                    component.originalName &&
                    component.originalName !== component.name.trim()
                  return (
                    <li
                      key={component.id}
                      onDragOver={(e) => {
                        e.preventDefault()
                        handleDragOverRow(component.id)
                      }}
                      className={`flex items-center gap-2 px-2 py-1.5 ${
                        dragId === component.id ? 'opacity-40' : ''
                      }`}
                    >
                      <span
                        draggable
                        onDragStart={(e) => {
                          e.dataTransfer.effectAllowed = 'move'
                          // ドラッグ像はハンドルだけでなく行全体にする
                          const row = e.currentTarget.closest('li')
                          if (row) e.dataTransfer.setDragImage(row, 16, 16)
                          setDragId(component.id)
                        }}
                        onDragEnd={() => setDragId(null)}
                        title={t('componentsPage.dragHandle')}
                        className="px-1 text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing"
                      >
                        <GripVertical className="h-4 w-4" />
                      </span>
                      <input
                        type="text"
                        value={component.name}
                        onChange={(e) =>
                          handleRename(component.id, e.target.value)
                        }
                        className="flex-1 px-2 py-1.5 text-sm text-gray-900 border border-transparent rounded hover:border-gray-200 focus:border-gray-300 focus:outline-none"
                      />
                      {renamed && (
                        <span
                          className="text-xs text-gray-400 whitespace-nowrap"
                          title={t('componentsPage.renamedHint')}
                        >
                          ← {component.originalName}
                        </span>
                      )}
                      <button
                        onClick={() => handleMove(component.id, -1)}
                        disabled={pos === 0}
                        className="text-gray-300 enabled:hover:text-gray-600 disabled:opacity-30 transition-colors px-1"
                        title={t('componentsPage.moveUp')}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleMove(component.id, 1)}
                        disabled={pos === list.length - 1}
                        className="text-gray-300 enabled:hover:text-gray-600 disabled:opacity-30 transition-colors px-1"
                        title={t('componentsPage.moveDown')}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleRemove(component.id)}
                        className="text-gray-300 hover:text-red-500 transition-colors px-2"
                        title={t('detail.removeComponent')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </li>
                  )
                })}
            </ul>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newNames[category]}
                onChange={(e) =>
                  setNewNames((prev) => ({
                    ...prev,
                    [category]: e.target.value,
                  }))
                }
                onKeyDown={(e) => {
                  // IME変換確定のEnterでは追加しない（確定文字列が入力欄に残るのを防ぐ）
                  if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                    handleAdd(category)
                  }
                }}
                placeholder={t('componentsPage.namePlaceholder')}
                className={inputClass}
              />
              <button
                onClick={() => handleAdd(category)}
                disabled={!newNames[category].trim()}
                className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              >
                <Plus className="h-4 w-4" />
                {t('detail.addComponent')}
              </button>
            </div>
          </section>
        ))}
      </div>

      {/* 削除は全機材の登録データ（S/N・メモ）ごと消えるため確認する */}
      <ConfirmDialog
        open={confirmOpen}
        title={t('componentsPage.confirmTitle')}
        message={t('componentsPage.confirmMessage', {
          names: removedNames.join('、'),
        })}
        confirmLabel={t('detail.save')}
        danger
        busy={saveMutation.isPending}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => saveMutation.mutate()}
      />
    </div>
  )
}
